import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Bank, CheckCircle, WarningCircle, ArrowSquareOut } from "@phosphor-icons/react";
import { toast } from "sonner";

function payoutState(status) {
  if (!status) return "loading";
  if (!status.connected) return "not_connected";
  if (status.payouts_enabled) return "ready";
  return "onboarding_incomplete";
}

export default function Payouts() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [params] = useSearchParams();

  const load = () => api.get("/connect/status").then((r) => setStatus(r.data));
  useEffect(() => {
    load();
    if (params.get("connected")) toast.success("Bank connected");
    if (params.get("refresh")) toast.info("Please complete onboarding again");
  }, []);

  const onboard = async () => {
    setBusy(true);
    try {
      const r = await api.post("/connect/onboard", { origin_url: window.location.origin });
      window.location.href = r.data.onboarding_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); setBusy(false); }
  };

  const good = status?.payouts_enabled;
  const state = payoutState(status);
  return (
    <div className="max-w-2xl space-y-5">
      <div>
        <div className="label-caps">Money</div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900">Payouts</h1>
        <p className="text-slate-600 mt-1">Connect a bank account (via Stripe) to receive wages directly after each completed shift.</p>
      </div>

      <div className="card-tactile p-6">
        <div className="flex items-start gap-4">
          <div className={`w-12 h-12 rounded-md flex items-center justify-center shrink-0 ${good ? "bg-emerald-100" : "bg-slate-100"}`}>
            <Bank size={24} className={good ? "text-emerald-600" : "text-slate-500"} />
          </div>
          <div className="flex-1 min-w-0">
            {state === "loading" && <div className="text-slate-500">Loading…</div>}
            {state === "not_connected" && (
              <>
                <div className="font-display text-xl font-bold text-slate-900">Not connected</div>
                <div className="text-sm text-slate-600 mt-1">Onboard with Stripe Express in a few minutes to enable direct payouts.</div>
                <Button data-testid="connect-onboard-btn" onClick={onboard} disabled={busy} className="mt-4 bg-blue-600 hover:bg-blue-700 h-11">
                  {busy ? "Opening…" : "Connect bank account"} <ArrowSquareOut size={16} className="ml-1.5"/>
                </Button>
              </>
            )}
            {state === "ready" && (
              <>
                <div className="flex items-center gap-2"><CheckCircle size={20} weight="fill" className="text-emerald-600"/><span className="font-display text-xl font-bold text-slate-900">Ready to receive payouts</span></div>
                <div className="text-sm text-slate-600 mt-1">Your Stripe account is verified. Wages will be transferred after each paid shift.</div>
                <div className="text-xs text-slate-500 mt-2">Account: {status.account_id}</div>
              </>
            )}
            {state === "onboarding_incomplete" && (
              <>
                <div className="flex items-center gap-2"><WarningCircle size={20} weight="fill" className="text-yellow-600"/><span className="font-display text-xl font-bold text-slate-900">Onboarding incomplete</span></div>
                <div className="text-sm text-slate-600 mt-1">Finish the Stripe onboarding form to enable payouts.</div>
                <Button data-testid="connect-continue-btn" onClick={onboard} disabled={busy} className="mt-4 bg-blue-600 hover:bg-blue-700 h-11">
                  {busy ? "Opening…" : "Continue onboarding"} <ArrowSquareOut size={16} className="ml-1.5"/>
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="text-xs text-slate-500">Powered by Stripe Express (test mode). No real money will move.</div>
    </div>
  );
}
